import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Heart, Activity, Pill, Clock, AlertTriangle, Brain,
  Calendar, Phone, ChevronRight, CheckCircle2, Circle,
  Stethoscope, MessageCircle, BookOpen, TrendingUp, Sun,
  Moon, Coffee, Utensils, Droplets, Thermometer, Wind
} from "lucide-react";

/**
 * Personalized Caregiver Dashboard
 * Patient at a Glance, Daily Medication Checklist, AI Symptom Check
 * Prioritizes readability and minimizes cognitive load
 */
export default function CaregiverDashboard({ patientId }) {
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [medications, setMedications] = useState([]);
  const [checkedMeds, setCheckedMeds] = useState({});
  const [vitals, setVitals] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const loadDashboardData = useCallback(async () => {
    try {
      // Load patient data, medications, and vitals in parallel
      const [patientRes, medsRes] = await Promise.all([
        api.get(`/patients/${patientId || 'current'}`).catch(() => ({ data: null })),
        api.get(`/medications/schedule/${patientId || 'current'}`).catch(() => ({ data: { medications: [] } }))
      ]);

      if (patientRes.data) setPatient(patientRes.data);
      if (medsRes.data?.medications) setMedications(medsRes.data.medications);

      // Load today's checked medications from localStorage
      const today = new Date().toDateString();
      const savedChecks = localStorage.getItem(`med_checks_${today}`);
      if (savedChecks) {
        setCheckedMeds(JSON.parse(savedChecks));
      }

    } catch (error) {
      console.error("Dashboard load error:", error);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const toggleMedication = (medId, timeSlot) => {
    const key = `${medId}_${timeSlot}`;
    const newChecked = { ...checkedMeds, [key]: !checkedMeds[key] };
    setCheckedMeds(newChecked);
    
    // Save to localStorage
    const today = new Date().toDateString();
    localStorage.setItem(`med_checks_${today}`, JSON.stringify(newChecked));
    
    if (!checkedMeds[key]) {
      toast.success("Medication marked as given");
    }
  };

  const getMedicationProgress = () => {
    const totalSlots = medications.reduce((acc, med) => acc + (med.times?.length || 1), 0);
    const checkedCount = Object.values(checkedMeds).filter(Boolean).length;
    return totalSlots > 0 ? Math.round((checkedCount / totalSlots) * 100) : 0;
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getTimeIcon = (time) => {
    if (!time) return Clock;
    const hour = parseInt(time.split(":")[0]);
    if (hour >= 5 && hour < 12) return Sun;
    if (hour >= 12 && hour < 17) return Coffee;
    if (hour >= 17 && hour < 21) return Utensils;
    return Moon;
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 border-[#4A7C6F] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="caregiver-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#4A7C6F] to-[#6B9B8A] rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="w-14 h-14 border-2 border-white/30">
              <AvatarFallback className="bg-white/20 text-white text-xl">
                {patient?.first_name?.charAt(0) || "P"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold">
                Good {getGreeting()}, Caregiver
              </h1>
              <p className="text-white/80">
                {patient ? `Caring for ${patient.first_name}` : "Your caregiving companion"}
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4" />
            <span className="text-sm">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column: Patient at a Glance */}
        <div className="lg:col-span-1 space-y-6">
          {/* Patient Status Card */}
          <Card className="border-0 shadow-md" data-testid="patient-glance">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Heart className="w-5 h-5 text-rose-500" />
                Patient at a Glance
              </CardTitle>
              {patient && (
                <CardDescription>
                  {patient.first_name} {patient.last_name}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Comfort Level */}
              <div className="p-4 bg-[#E8F4F0] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Current Comfort Level</span>
                  <Badge className="bg-green-100 text-green-700">Stable</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={75} className="flex-1 h-3" />
                  <span className="text-lg font-bold text-[#4A7C6F]">7.5/10</span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <Thermometer className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                  <p className="text-lg font-bold text-slate-800">98.6°F</p>
                  <p className="text-xs text-slate-500">Temperature</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <Activity className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                  <p className="text-lg font-bold text-slate-800">72 bpm</p>
                  <p className="text-xs text-slate-500">Heart Rate</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <Wind className="w-5 h-5 mx-auto text-teal-500 mb-1" />
                  <p className="text-lg font-bold text-slate-800">18</p>
                  <p className="text-xs text-slate-500">Resp. Rate</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <Droplets className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
                  <p className="text-lg font-bold text-slate-800">Good</p>
                  <p className="text-xs text-slate-500">Hydration</p>
                </div>
              </div>

              {/* Last Updated */}
              <p className="text-xs text-slate-400 text-center">
                Last updated: {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </p>
            </CardContent>
          </Card>

          {/* AI Symptom Check Button */}
          <Link to="/symptoms" data-testid="ai-symptom-check-btn">
            <Card className="border-2 border-[#4A7C6F] bg-gradient-to-r from-[#E8F4F0] to-white hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#4A7C6F] flex items-center justify-center">
                    <Brain className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#4A7C6F] text-lg">AI Symptom Check</h3>
                    <p className="text-sm text-slate-600">Get guidance on managing symptoms</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-[#4A7C6F]" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Center Column: Medication Checklist */}
        <div className="lg:col-span-1">
          <Card className="border-0 shadow-md h-full" data-testid="medication-checklist">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Pill className="w-5 h-5 text-purple-500" />
                  Today&apos;s Medications
                </CardTitle>
                <Badge variant="outline" className="text-[#4A7C6F]">
                  {getMedicationProgress()}% Done
                </Badge>
              </div>
              <Progress value={getMedicationProgress()} className="h-2 mt-2" />
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {/* Sample Medications - In production, this would come from API */}
                  {[
                    { id: "1", name: "Morphine", dose: "10mg", times: ["08:00", "14:00", "20:00"], instructions: "With food" },
                    { id: "2", name: "Ondansetron", dose: "4mg", times: ["08:00", "20:00"], instructions: "For nausea" },
                    { id: "3", name: "Lorazepam", dose: "0.5mg", times: ["20:00"], instructions: "As needed for anxiety" },
                    { id: "4", name: "Docusate", dose: "100mg", times: ["08:00", "20:00"], instructions: "Stool softener" },
                    { id: "5", name: "Omeprazole", dose: "20mg", times: ["08:00"], instructions: "Before breakfast" },
                  ].map((med) => (
                    <div key={med.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-800">{med.name}</h4>
                          <p className="text-sm text-slate-500">{med.dose} — {med.instructions}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {med.times.map((time) => {
                          const TimeIcon = getTimeIcon(time);
                          const key = `${med.id}_${time}`;
                          const isChecked = checkedMeds[key];
                          return (
                            <button
                              key={time}
                              onClick={() => toggleMedication(med.id, time)}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                                isChecked
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : "bg-white text-slate-600 border border-slate-200 hover:border-[#4A7C6F]"
                              }`}
                            >
                              {isChecked ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <Circle className="w-4 h-4" />
                              )}
                              <TimeIcon className="w-3 h-3" />
                              {formatTime(time)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Quick Actions & Alerts */}
        <div className="lg:col-span-1 space-y-6">
          {/* Quick Actions */}
          <Card className="border-0 shadow-md" data-testid="quick-actions">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Link to="/journal">
                <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3">
                  <BookOpen className="w-5 h-5 text-amber-500" />
                  <div className="text-left">
                    <p className="font-medium">Log Observation</p>
                    <p className="text-xs text-slate-500">Record symptoms or changes</p>
                  </div>
                </Button>
              </Link>
              <Link to="/patient-messages">
                <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Message Care Team</p>
                    <p className="text-xs text-slate-500">Ask questions or update</p>
                  </div>
                </Button>
              </Link>
              <Link to="/schedule-appointment">
                <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3">
                  <Calendar className="w-5 h-5 text-green-500" />
                  <div className="text-left">
                    <p className="font-medium">Schedule Visit</p>
                    <p className="text-xs text-slate-500">Request nurse or aide visit</p>
                  </div>
                </Button>
              </Link>
              <a href="tel:703-578-7108">
                <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3 border-rose-200 hover:bg-rose-50">
                  <Phone className="w-5 h-5 text-rose-500" />
                  <div className="text-left">
                    <p className="font-medium text-rose-600">Call Urgent Line</p>
                    <p className="text-xs text-slate-500">24/7 hospice support</p>
                  </div>
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Alerts & Reminders */}
          <Card className="border-0 shadow-md" data-testid="alerts-reminders">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Reminders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Next Nurse Visit</p>
                    <p className="text-xs text-amber-600">Tomorrow at 10:00 AM</p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Pill className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Medication Refill</p>
                    <p className="text-xs text-blue-600">Morphine due in 3 days</p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Comfort Trend</p>
                    <p className="text-xs text-green-600">Improving over past week</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
}
